import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Download, Edit, X, Send, SquareArrowUpRightIcon } from 'lucide-react';
import { Tabs, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { ScrollableTabsList } from '@/components/ScrollableTabsList';
import { ClientDetailsDeliveryContent } from './ClientDetailsDeliveryContent';
import { PickupInformationContent } from './PickupInformationContent';
import { DeliveryInformationContent } from './DeliveryInformationContent';
import { DeliveryUpdatesContent } from './DeliveryUpdatesContent';
import { BillingIntakeContent, BillingWorkOrderContent } from './BillingDeliveryContent';
import { ShareDeliveryUpdateImageDialog } from './ShareDeliveryUpdateImageDialog';
import { useNavigate } from 'react-router-dom';
import { useState, Fragment, useMemo, useEffect } from 'react';
import Loader from '@/components/Loader';
import { deliveryService, useDeliveryDetail, useSendDeliveryInvoiceMail } from '@/services/deliveryService';
import { isWorkOrderDetail } from '@/types/deliveryDetail.types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { hasFeature } from '@/services/featureService';
import { downloadFilefromBuffer } from '@/lib/utils';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import AlertBox from '@/components/AlertBox';
import { getDeliveryClientDisplayName, getDeliveryClientInitials } from './deliveryDetails.utils';
import { useQueryClient } from '@tanstack/react-query';
import type { DeliveryWorkOrderDetailResponse } from '@/types/deliveryDetail.types';
import { useUserStore } from '@/stores/user';
import { canManageDeliveryInvoice } from '@/pages/delivery/invoice/deliveryInvoiceAccess';
import { buildInvoiceRoute } from '@/pages/delivery/invoice/invoiceRouteHelpers';

const iconClasses = 'h-[20px] w-[20px] cursor-pointer';

const getDeliveryUpdateEmailDisabledReason = (data: DeliveryWorkOrderDetailResponse): string | null => {
  const updates = data.deliveryUpdates?.updates ?? [];
  if (updates.length === 0) {
    return 'No delivery updates exist.';
  }
  if (!data.clientDetails.contactEmail) {
    return 'No contact email is available for this work order.';
  }
  return null;
};

const getInvoiceEmailErrorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { error?: { message?: string } } } }).response;
    return response?.data?.error?.message ?? 'Failed to send invoice email.';
  }

  return 'Failed to send invoice email.';
};

export const DeliveryDetails = ({
  onCloseClick,
  id,
  fallbackClientName,
}: {
  onCloseClick: () => void;
  id: string;
  fallbackClientName?: string | null;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userStore = useUserStore();
  const canEditInvoice = canManageDeliveryInvoice(userStore.user);
  const { data, isFetching, isError } = useDeliveryDetail(id);
  const [activeTab, setActiveTab] = useState('CLIENT_DETAILS');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const sentInvoiceMail = useSendDeliveryInvoiceMail();
  const [invoiceConfirmationDialog, setInvoiceConfirmationDialog] = useState(false);
  const [displayRecipientEmails, setDisplayRecipientEmails] = useState(false);

  useEffect(() => {
    setActiveTab('CLIENT_DETAILS');
  }, [id]);

  // Check if feature flags allow certain actions
  const canRead = hasFeature('delivery.read');
  const isDeliveryUpdatesEnabled = hasFeature('delivery.deliveryUpdates');
  const isShareUpdatesEnabled = hasFeature('delivery.shareUpdates');

  if (!canRead) {
    return null;
  }

  const isWorkOrder = data && isWorkOrderDetail(data);
  const clientDisplayName = getDeliveryClientDisplayName(data?.summary.clientName, fallbackClientName);
  const clientInitials = getDeliveryClientInitials(clientDisplayName);
  const deliveryUpdateEmailDisabledReason =
    isWorkOrder && isWorkOrderDetail(data) ? getDeliveryUpdateEmailDisabledReason(data) : null;
  const showEmailDeliveryUpdatesOption =
    isWorkOrder &&
    isWorkOrderDetail(data) &&
    isShareUpdatesEnabled &&
    data.actionFlags.canEmailDeliveryUpdates;
  const showSendDropdown =
    isWorkOrder && isWorkOrderDetail(data) &&
    (showEmailDeliveryUpdatesOption || data.actionFlags.canEmailInvoice);

  const invoiceRecipientEmails = Array.from(
    new Set(
      [isWorkOrder ? data?.clientDetails.contactEmail : null].filter(
        (email): email is string => Boolean(email)
      )
    )
  );

  // Define tabs based on whether it's intake form or work order
  const tabs = isWorkOrder
    ? [
        { value: 'CLIENT_DETAILS', label: 'Client Details' },
        { value: 'PICKUP_INFORMATION', label: 'Pickup Information' },
        { value: 'DELIVERY_INFORMATION', label: 'Delivery Information' },
        ...(isDeliveryUpdatesEnabled
          ? [{ value: 'DELIVERY_UPDATES', label: 'Delivery Updates' }]
          : []),
        { value: 'BILLING', label: 'Billing' },
      ]
    : [
        { value: 'CLIENT_DETAILS', label: 'Client Details' },
        { value: 'PICKUP_INFORMATION', label: 'Pickup Information' },
        { value: 'DELIVERY_INFORMATION', label: 'Delivery Information' },
        { value: 'BILLING', label: 'Billing' },
      ];

  const tabWidth = tabs.length <= 3 ? `${100 / tabs.length}%` : '33.33%';

  const getStatusLabel = (status: string): string => {
    const labelMap: Record<string, string> = {
      OutForDelivery: 'Out for Delivery',
    };
    return labelMap[status] ?? status;
  };

  // Helper to get status pill styling
  const getStatusClassName = (status: string): string => {
    const statusMap: Record<string, string> = {
      Draft: 'bg-[#F5F5F5] text-[#676767]',
      Unassigned: 'bg-[#E2E8F0] text-[#475569]',
      Assigned: 'bg-[#EDF5FF] text-primary',
      OutForDelivery: 'bg-[#FEF08A] text-[#854D0E]',
      Delivered: 'bg-[#E3F2FD] text-[#1565C0]',
      Billed: 'bg-[#F3E5F5] text-[#6A1B9A]',
      Rescheduled: 'bg-[#FFEEBF] text-[#6C4A09]',
      Cancelled: 'bg-[#FFEBEE] text-[#C62828]',
    };
    return statusMap[status] || 'bg-gray-100 text-gray-700';
  };

  const editMenuItems = useMemo(() => {
    if (!isWorkOrder || !data || !isWorkOrderDetail(data)) {
      return [];
    }

    const items: { key: string; label: string; onSelect: () => void }[] = [];

    if (activeTab === 'BILLING' && canEditInvoice && data.billing?.invoice?.invoiceNumber) {
      items.push({
        key: 'edit-invoice',
        label: 'Edit Invoice',
        onSelect: () => {
          navigate(
            buildInvoiceRoute(
              data.billing.invoice.invoiceNumber,
              'edit',
              data.commonDetails.workOrderId
            )
          );
        },
      });
    }

    if (isDeliveryUpdatesEnabled && data.actionFlags.canEditDeliveryUpdates) {
      items.push({
        key: 'add',
        label: 'Add Delivery Updates',
        onSelect: () => {
          navigate(
            `/delivery/work-order/${data.commonDetails.workOrderId}/delivery-updates?mode=add`
          );
        },
      });
    }

    if (
      isDeliveryUpdatesEnabled &&
      data.actionFlags.canEditDeliveryUpdates &&
      (data.deliveryUpdates?.updates?.length ?? 0) > 0
    ) {
      items.push({
        key: 'edit-update',
        label: 'Edit Delivery Updates',
        onSelect: () => {
          const firstUpdateId = data.deliveryUpdates?.updates[0]?.updateId;
          if (!firstUpdateId) return;
          navigate(
            `/delivery/work-order/${data.commonDetails.workOrderId}/delivery-update/${firstUpdateId}?mode=edit`
          );
        },
      });
    }

    if (data.actionFlags.canEditWorkOrder) {
      items.push({
        key: 'edit-wo',
        label: 'Edit Work Order',
        onSelect: () => {
          navigate(`/delivery/work-order/${data.commonDetails.workOrderId}/edit`);
        },
      });
    }

    return items;
  }, [activeTab, canEditInvoice, data, isDeliveryUpdatesEnabled, isWorkOrder, navigate]);

  return (
    <Sheet modal={false} open={true}>
      <SheetContent
        hideCloseButton={true}
        className="w-[80vw] md:w-[500px] scrollbar-hide bg-white"
        side="right"
        tabIndex={-1}
      >
        <Loader isLoading={isFetching}>
          {isError || !data ? (
            <div className="flex h-full items-center relative justify-center w-full text-placeholder">
              <SheetHeader className="sr-only">
                <SheetTitle>Delivery Details</SheetTitle>
                <SheetDescription>Delivery details view</SheetDescription>
              </SheetHeader>
              <div className="border-[1px] p-[6px] absolute top-6 right-6 rounded-md" onClick={onCloseClick}>
                <X color="#676767" className="h-[20px] w-[20px] cursor-pointer" />
              </div>
              <span>No Results</span>
            </div>
          ) : (
            <Tabs className="w-full text-black" defaultValue={tabs?.[0]?.value} onValueChange={setActiveTab}>
              <SheetHeader className="flex flex-col gap-4 p-6 pb-0 sticky top-0 bg-white z-10">
                <SheetTitle className="flex flex-col gap-4 bg-white pb-0">
                  {/* Action buttons row */}
                  <div className="flex flex-row justify-between">
                    {isWorkOrder && data.summary.invoiceNumber && (
                      <span className="font-semibold text-xl">{`Invoice #: ${data.summary.invoiceNumber}`}</span>
                    )}
                    {!isWorkOrder && <span className="font-semibold text-xl">Intake Form</span>}
                    <div className="flex flex-row gap-2 h-[32px] [&_div]:border-[1px] [&_div]:p-[6px] [&_div]:rounded-md [&_div]:justify-center">
                      {/* Send button (work orders only) */}
                      {showSendDropdown && (
                          <DropdownMenu>
                            <DropdownMenuTrigger>
                              <div>
                                <Send color="#676767" className={iconClasses} />
                              </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="ml-[-100px] min-w-[180px]">
                              {showEmailDeliveryUpdatesOption && (
                                <DropdownMenuItem
                                  disabled={!!deliveryUpdateEmailDisabledReason}
                                  title={deliveryUpdateEmailDisabledReason ?? undefined}
                                  onSelect={(event) => {
                                    if (deliveryUpdateEmailDisabledReason) {
                                      event.preventDefault();
                                      return;
                                    }
                                    setShowShareDialog(true);
                                  }}
                                >
                                  <div className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]">
                                    <span>Email Delivery Updates</span>
                                    <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                                  </div>
                                </DropdownMenuItem>
                              )}
                              {data.actionFlags.canEmailInvoice && (
                                <>
                                  {showEmailDeliveryUpdatesOption && (
                                    <DropdownMenuSeparator className="h-[2px]" />
                                  )}
                                  <DropdownMenuItem onSelect={() => setInvoiceConfirmationDialog(true)}>
                                    <div className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]">
                                      <span>Email Invoice</span>
                                      <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                                    </div>
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}

                      {/* Export Delivery Sheet button (work orders only) */}
                      {isWorkOrder && data.actionFlags.canExportDeliverySheet && (
                        <div
                          onClick={async () => {
                            const workOrderId = data.commonDetails.workOrderId;
                            if (!workOrderId) {
                              toast({
                                variant: 'destructive',
                                description: 'Work order ID not found.',
                                duration: 3000,
                              });
                              return;
                            }

                            const { data: pdfData, error } = await deliveryService.exportDeliverySheet(workOrderId);
                            if (error) {
                              displayError(error, 5000);
                            } else if (pdfData) {
                              const fileName = `${data.commonDetails.workOrderNumber ?? 'delivery-sheet'}.pdf`;
                              downloadFilefromBuffer(pdfData, fileName, 'PDF');
                            }
                          }}
                          className="h-[32px]"
                        >
                          <Download color="#676767" className={iconClasses} />
                        </div>
                      )}

                      {/* Edit button */}
                      {isWorkOrder ? (
                        editMenuItems.length > 0 ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger>
                            <div>
                              <Edit color="#676767" className={iconClasses} />
                            </div>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="ml-[-100px] min-w-[180px]">
                            {editMenuItems.map((item, index) => (
                              <Fragment key={item.key}>
                                {index > 0 && <DropdownMenuSeparator className="h-[2px]" />}
                                <DropdownMenuItem onSelect={item.onSelect}>
                                  <div className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]">
                                    <span>{item.label}</span>
                                    <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                                  </div>
                                </DropdownMenuItem>
                              </Fragment>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        ) : null
                      ) : (
                        // Intake Form Edit - Direct Icon Button
                        data.actionFlags.canEditIntakeForm && (
                          <div
                            onClick={() => {
                              navigate(`/delivery/${data.commonDetails.intakeId}/edit`);
                            }}
                            className="h-[32px]"
                          >
                            <Edit color="#676767" className={iconClasses} />
                          </div>
                        )
                      )}

                      {/* Close button */}
                      <div onClick={onCloseClick} className="h-[32px]">
                        <X color="#676767" className={iconClasses} />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1">
                    {/* Client info section */}
                    <div className="flex flex-row gap-3 pt-2">
                      <div className="bg-[#9747FF33] rounded-md text-normal font-semibold p-2 h-[48px] w-[48px] flex justify-center text-[#55288F] items-center flex-shrink-0">
                        <span>{clientInitials}</span>
                      </div>

                      <div className="flex flex-col w-full min-w-0">
                        <div className="flex flex-row justify-between items-start gap-2">
                          <div className="flex flex-col min-w-0">
                            <span className="text-base font-semibold truncate" title={clientDisplayName}>
                              {clientDisplayName}
                            </span>
                            <span className="text-sm font-normal text-gray-500">#{data.summary.clientCode}</span>
                          </div>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusClassName(
                              data.summary.status
                            )}`}
                          >
                            {getStatusLabel(data.summary.status)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-[#969696] font-[400] text-sm">
                      <span style={{ overflowWrap: 'anywhere' }}>{data.summary.clientAddress}</span>
                    </div>
                  </div>
                  <SheetDescription className="sr-only">Delivery details view</SheetDescription>
                </SheetTitle>

                {/* Cancellation reason banner */}
                {data.summary.status === 'Cancelled' && data.commonDetails.cancellationReason && (
                  <div className="mx-0 rounded-md bg-[#F5F5F5] border border-[#FFCDD2] px-4 py-3 flex flex-col gap-1">
                    <span className="text-xs font-semibold uppercase tracking-wide">Cancelation Reason</span>
                    <span className="text-sm">{data.commonDetails.cancellationReason}</span>
                    {data.commonDetails.cancelledBy && (
                      <span className="text-xs">
                        By {data.commonDetails.cancelledBy.name}
                        {data.commonDetails.cancelledAt
                          ? ` · ${new Date(data.commonDetails.cancelledAt).toLocaleDateString()}`
                          : ''}
                      </span>
                    )}
                  </div>
                )}

                {/* Tabs */}
                <ScrollableTabsList className="flex flex-row bg-transparent p-0 border-b h-auto justify-start">
                  {tabs.map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      style={{ width: tabWidth }}
                      className="flex-none rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-2 py-3 text-sm font-medium text-muted-foreground data-[state=active]:text-primary"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </ScrollableTabsList>
              </SheetHeader>

              {/* Tab content */}
              <div className="w-full !mt-0 pb-2 text-[#0A0A0A]">
                <TabsContent value="CLIENT_DETAILS" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <ClientDetailsDeliveryContent data={data.clientDetails} isPrepaid={data.commonDetails.isPrepaid} />
                </TabsContent>
                <TabsContent value="PICKUP_INFORMATION" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <PickupInformationContent data={data.pickupInformation} />
                </TabsContent>
                <TabsContent value="DELIVERY_INFORMATION" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <DeliveryInformationContent data={data.deliveryInformation} commonDetails={data.commonDetails} />
                </TabsContent>
                {isWorkOrder && isDeliveryUpdatesEnabled && (
                  <TabsContent value="DELIVERY_UPDATES" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                    <DeliveryUpdatesContent
                      data={data.deliveryUpdates}
                      workOrderId={data.commonDetails.workOrderId}
                      intakeId={data.commonDetails.intakeId}
                      clientName={clientDisplayName}
                      canEditDeliveryUpdates={data.actionFlags.canEditDeliveryUpdates}
                    />
                  </TabsContent>
                )}
                <TabsContent value="BILLING" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  {isWorkOrder ? (
                    <BillingWorkOrderContent
                      data={data.billing}
                      workOrderId={data.commonDetails.workOrderId}
                    />
                  ) : (
                    <BillingIntakeContent data={data.billing} />
                  )}
                </TabsContent>
              </div>
            </Tabs>
          )}
        </Loader>

        {showShareDialog && isWorkOrder && isWorkOrderDetail(data) && (
          <ShareDeliveryUpdateImageDialog
            onCloseClick={() => setShowShareDialog(false)}
            updateIds={data.deliveryUpdates.updates.map((u) => u.updateId)}
            workOrderId={data.commonDetails.workOrderId}
            recipientEmail={data.clientDetails.contactEmail ?? ''}
            onEmailSent={() => {
              void queryClient.invalidateQueries({ queryKey: ['delivery-detail', id] });
            }}
          />
        )}

        {/* Invoice Confirmation Dialog */}
        {invoiceConfirmationDialog && isWorkOrder && isWorkOrderDetail(data) && (
          <AlertBox
            title="Are you sure?"
            description={
              <div>
                <span>Are you sure you want to send the invoice? </span>
                {invoiceRecipientEmails.length === 0 ? (
                  <span className="text-muted-foreground">No recipients configured.</span>
                ) : displayRecipientEmails ? (
                  <div className="flex flex-col gap-2">
                    <span>{invoiceRecipientEmails.join(', ')}</span>
                    <span onClick={() => setDisplayRecipientEmails(false)} className="text-primary cursor-pointer">
                      Hide Recipients
                    </span>
                  </div>
                ) : (
                  <span onClick={() => setDisplayRecipientEmails(true)} className="text-primary cursor-pointer">
                    View Recipients
                  </span>
                )}
              </div>
            }
            isLoading={sentInvoiceMail.isPending}
            open={invoiceConfirmationDialog}
            cancelBtnLabel="No"
            confirmBtnLabel="Yes"
            cancelBtnClick={() => {
              setInvoiceConfirmationDialog(false);
              setDisplayRecipientEmails(false);
            }}
            confirmBtnClick={() => {
              sentInvoiceMail.mutate(
                { invoiceNumber: data.summary.invoiceNumber ?? data.billing.invoice.invoiceNumber },
                {
                  onSuccess: (response) => {
                    toast({
                      description: response.message,
                    });
                    setInvoiceConfirmationDialog(false);
                    setDisplayRecipientEmails(false);
                    void queryClient.invalidateQueries({ queryKey: ['delivery-detail', id] });
                  },
                  onError: (error) => {
                    toast({
                      description: getInvoiceEmailErrorMessage(error),
                      variant: 'destructive',
                    });
                    setInvoiceConfirmationDialog(false);
                    setDisplayRecipientEmails(false);
                  },
                }
              );
            }}
          />
        )}
      </SheetContent>
    </Sheet>
  );
};
